import { useMemo } from "react";
import { gql, TypedDocumentNode, useQuery } from "@apollo/client";
import type { ApolloError } from "@apollo/client";
import { getInteractionDisplayName } from "@/components/interactions/_utils";
import type { InteractionMode } from "@/components/interactions/types";
import { CRM_NOTE_FIELDS_FRAGMENT } from "@/fragments";
import {
  getInteractionWithLabel,
  getApplicationDisplayName,
  getCareerPlanDisplayName,
  getOrderDisplayName,
  getPaymentDisplayName,
  getPersonDisplayName
} from "@/features/global/components/ReferencesTable/types";
import { useFlags } from "@/hooks/useFeatureFlags";
import type { CRMNote } from "@/types";

export type ReferenceLookupContext =
  | { type: "Person"; id: string }
  | {
      type: "Facility";
      id: string;
      legacyId?: string | null;
      name: string;
      city?: string | null;
      state?: string | null;
    }
  | {
      type: "Event";
      id: string;
      slug: string;
      title: string;
      status?: string | null;
      startsAt?: string | null;
    }
  | null;

export interface ReferencePickerItem {
  __typename:
    | "CRMNote"
    | "CRMInteraction"
    | "BackofficeMembershipApplication"
    | "CommerceOrder"
    | "CommercePayment"
    | "CRMPerson"
    | "CRMEvent"
    | "CRMTaskFacility"
    | "BackofficeCareerPlan";
  id: string;
  displayName: string;
  date: string | null;
  notableType?: string | null;
  authorName?: string | null;
  categories?: string[] | null;
  applicationType?: string | null;
  applicationStatus?: string | null;
  orderStatus?: string | null;
  paymentStatus?: string | null;
  personPgaId?: string | null;
  eventStatus?: string | null;
  eventSlug?: string | null;
  careerPlanStatus?: string | null;
  facilityCity?: string | null;
  facilityState?: string | null;
}

const PERSON_NOTES_FOR_REFERENCE_QUERY = gql`
  query PersonNotesForReference($pgaId: ID!, $first: Int) {
    personNotes(pgaId: $pgaId, first: $first) {
      nodes {
        ...CRMNoteFields
      }
    }
  }
  ${CRM_NOTE_FIELDS_FRAGMENT}
`;

export interface FacilityNotesForReferenceResponse {
  facilityNotes: {
    nodes: CRMNote[];
  };
}

export interface FacilityNotesForReferenceVariables {
  id: string;
  first: number;
}

export const FACILITY_NOTES_FOR_REFERENCE_QUERY: TypedDocumentNode<
  FacilityNotesForReferenceResponse,
  FacilityNotesForReferenceVariables
> = gql`
  query FacilityNotesForReference($id: ID!, $first: Int) {
    facilityNotes(id: $id, first: $first) {
      nodes {
        ...CRMNoteFields
      }
    }
  }
  ${CRM_NOTE_FIELDS_FRAGMENT}
`;

export interface EventNotesForReferenceResponse {
  eventNotes: {
    nodes: CRMNote[];
  };
}

export interface EventNotesForReferenceVariables {
  slug: string;
  first: number;
}

export const EVENT_NOTES_FOR_REFERENCE_QUERY: TypedDocumentNode<
  EventNotesForReferenceResponse,
  EventNotesForReferenceVariables
> = gql`
  query EventNotesForReference($slug: String!, $first: Int) {
    eventNotes(slug: $slug, first: $first) {
      nodes {
        ...CRMNoteFields
      }
    }
  }
  ${CRM_NOTE_FIELDS_FRAGMENT}
`;

const PERSON_INTERACTIONS_FOR_REFERENCE_QUERY = gql`
  query PersonInteractionsForReference($pgaId: ID!, $first: Int) {
    person(pgaId: $pgaId) {
      id
      interactions(search: $pgaId, first: $first) {
        nodes {
          id
          relatedTo
          mode
          lastModifiedDate
          createdAt
          categories
          owner {
            name
          }
          participants {
            pgaId
            firstName
            lastName
          }
        }
      }
    }
  }
`;

const PERSON_APPLICATIONS_FOR_REFERENCE_QUERY = gql`
  query PersonApplicationsForReference($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      pgaId
      firstName
      lastName
      displayName
      profilePhoto
      email
      membershipApplications {
        id
        applicationType
        status
        createdAt
        person {
          pgaId
          displayName
        }
      }
    }
  }
`;

const PERSON_ORDERS_FOR_REFERENCE_QUERY = gql`
  query PersonOrdersForReference($search: String!, $first: Int) {
    commerceOrders(first: $first, filters: { search: $search }) {
      nodes {
        id
        description
        status
        dueDate
        balance
        updatedAt
        person {
          pgaId
          firstName
          lastName
          displayName
        }
      }
    }
  }
`;

const PERSON_PAYMENTS_FOR_REFERENCE_QUERY = gql`
  query PersonPaymentsForReference($search: String!, $first: Int) {
    commercePayments(first: $first, filters: { search: $search }) {
      nodes {
        id
        amountCents
        type
        status
        paidAt
        payer {
          id
          name
        }
      }
    }
  }
`;

interface CareerPlanNode {
  id: string;
  title?: string | null;
  status?: string | null;
  updatedAt?: string | null;
}

export interface PersonCareerPlansForReferenceResponse {
  person: {
    careerPlans: CareerPlanNode[];
  } | null;
}

export interface PersonCareerPlansForReferenceVariables {
  pgaId: string;
}

export const PERSON_CAREER_PLANS_FOR_REFERENCE_QUERY: TypedDocumentNode<
  PersonCareerPlansForReferenceResponse,
  PersonCareerPlansForReferenceVariables
> = gql`
  query PersonCareerPlansForReference($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      careerPlans {
        id
        title
        status
        updatedAt
      }
    }
  }
`;

interface InteractionNode {
  id: string;
  relatedTo: string;
  mode: InteractionMode;
  lastModifiedDate: string;
  createdAt: string;
  categories?: string[] | null;
  owner?: { name: string } | null;
  participants?: { pgaId: string; firstName?: string; lastName?: string }[] | null;
}

interface ApplicationNode {
  id: string;
  applicationType: string;
  status: string;
  createdAt: string;
  person: { pgaId: string; displayName: string };
}

interface OrderNode {
  id: string;
  description?: string;
  status: string;
  dueDate?: string;
  balance?: number;
  updatedAt?: string;
  person?: { pgaId: string; firstName?: string; lastName?: string; displayName?: string };
}

interface PaymentNode {
  id: string;
  amountCents?: number;
  type?: string;
  status: string;
  paidAt?: string;
  payer?: { id: string; name: string };
}

interface PersonNode {
  id: string;
  pgaId?: string | null;
  firstName?: string;
  lastName?: string;
  displayName?: string | null;
  profilePhoto?: string | null;
  email?: string | null;
}

function toNotePickerItems(notes: CRMNote[]): ReferencePickerItem[] {
  return notes.map((n) => ({
    __typename: "CRMNote",
    id: n.id,
    displayName: n.displayTitle || n.title || "Untitled Note",
    date: n.updatedAt || n.createdAt,
    notableType: n.notableType || null,
    authorName: n.authorName || null
  }));
}

function toEventPickerItems(
  event: Extract<ReferenceLookupContext, { type: "Event" }>,
  notes: CRMNote[]
): ReferencePickerItem[] {
  const eventItem: ReferencePickerItem = {
    __typename: "CRMEvent",
    id: event.id,
    displayName: event.title,
    date: event.startsAt || null,
    eventStatus: event.status ?? null,
    eventSlug: event.slug
  };

  return [eventItem, ...toNotePickerItems(notes)];
}

function toFacilityPickerItems(
  facility: Extract<ReferenceLookupContext, { type: "Facility" }>,
  notes: CRMNote[]
): ReferencePickerItem[] {
  const facilityItem: ReferencePickerItem = {
    __typename: "CRMTaskFacility",
    id: facility.id,
    displayName: facility.name,
    date: null,
    facilityCity: facility.city ?? null,
    facilityState: facility.state ?? null
  };

  return [facilityItem, ...toNotePickerItems(notes)];
}

function toPickerItems(
  person: PersonNode | null,
  notes: CRMNote[],
  interactions: InteractionNode[],
  applications: ApplicationNode[],
  orders: OrderNode[],
  payments: PaymentNode[],
  careerPlans: CareerPlanNode[]
): ReferencePickerItem[] {
  const noteItems = toNotePickerItems(notes);

  const interactionItems: ReferencePickerItem[] = interactions.map((i) => ({
    __typename: "CRMInteraction",
    id: i.id,
    displayName: getInteractionDisplayName(i, getInteractionWithLabel(i)),
    date: i.lastModifiedDate || i.createdAt,
    categories: i.categories || null
  }));

  const applicationItems: ReferencePickerItem[] = applications.map((a) => ({
    __typename: "BackofficeMembershipApplication",
    id: a.id,
    displayName: getApplicationDisplayName(a.applicationType, a.person?.displayName),
    date: a.createdAt,
    applicationType: a.applicationType,
    applicationStatus: a.status
  }));

  const orderItems: ReferencePickerItem[] = orders.map((o) => ({
    __typename: "CommerceOrder" as const,
    id: o.id,
    displayName: getOrderDisplayName(o),
    date: o.dueDate || o.updatedAt || null,
    orderStatus: o.status
  }));

  const paymentItems: ReferencePickerItem[] = payments.map((p) => ({
    __typename: "CommercePayment" as const,
    id: p.id,
    displayName: getPaymentDisplayName(p),
    date: p.paidAt || null,
    paymentStatus: p.status
  }));

  const personItems: ReferencePickerItem[] = person
    ? [
        {
          __typename: "CRMPerson" as const,
          id: person.id,
          displayName: getPersonDisplayName(person),
          date: null,
          personPgaId: person.pgaId ?? null
        }
      ]
    : [];

  const careerPlanItems: ReferencePickerItem[] = careerPlans.map((plan) => ({
    __typename: "BackofficeCareerPlan" as const,
    id: plan.id,
    displayName: getCareerPlanDisplayName(plan.title),
    date: plan.updatedAt || null,
    careerPlanStatus: plan.status ?? null
  }));

  return [
    ...personItems,
    ...noteItems,
    ...interactionItems,
    ...applicationItems,
    ...orderItems,
    ...paymentItems,
    ...careerPlanItems
  ];
}

const QUERY_LIMIT = 100;

interface UseReferencePickerItemsResult {
  items: ReferencePickerItem[];
  loading: boolean;
  error?: ApolloError;
  truncated: boolean;
}

export function useReferencePickerItems(lookupContext: ReferenceLookupContext): UseReferencePickerItemsResult {
  const flags = useFlags();
  const careerPlansEnabled = flags["pga-career-plan"] === true;
  const personPgaId = lookupContext?.type === "Person" ? lookupContext.id : null;
  const facilityId = lookupContext?.type === "Facility" ? lookupContext.legacyId || lookupContext.id : null;
  const eventSlug = lookupContext?.type === "Event" ? lookupContext.slug : null;

  const { data: notesData, loading: notesLoading } = useQuery(PERSON_NOTES_FOR_REFERENCE_QUERY, {
    variables: { pgaId: personPgaId, first: QUERY_LIMIT },
    skip: !personPgaId
  });

  const { data: interactionsData, loading: interactionsLoading } = useQuery(PERSON_INTERACTIONS_FOR_REFERENCE_QUERY, {
    variables: { pgaId: personPgaId, first: QUERY_LIMIT },
    skip: !personPgaId
  });

  const { data: applicationsData, loading: applicationsLoading } = useQuery(PERSON_APPLICATIONS_FOR_REFERENCE_QUERY, {
    variables: { pgaId: personPgaId },
    skip: !personPgaId
  });

  const { data: ordersData, loading: ordersLoading } = useQuery(PERSON_ORDERS_FOR_REFERENCE_QUERY, {
    variables: { search: personPgaId, first: QUERY_LIMIT },
    skip: !personPgaId
  });

  const { data: paymentsData, loading: paymentsLoading } = useQuery(PERSON_PAYMENTS_FOR_REFERENCE_QUERY, {
    variables: { search: personPgaId, first: QUERY_LIMIT },
    skip: !personPgaId
  });

  const { data: careerPlansData, loading: careerPlansLoading } = useQuery(PERSON_CAREER_PLANS_FOR_REFERENCE_QUERY, {
    variables: { pgaId: personPgaId ?? "" },
    skip: !personPgaId || !careerPlansEnabled
  });

  const {
    data: facilityNotesData,
    loading: facilityNotesLoading,
    error: facilityNotesError
  } = useQuery(FACILITY_NOTES_FOR_REFERENCE_QUERY, {
    variables: { id: facilityId ?? "", first: QUERY_LIMIT },
    skip: !facilityId
  });

  const {
    data: eventNotesData,
    loading: eventNotesLoading,
    error: eventNotesError
  } = useQuery(EVENT_NOTES_FOR_REFERENCE_QUERY, {
    variables: { slug: eventSlug ?? "", first: QUERY_LIMIT },
    skip: !eventSlug
  });

  const items = useMemo(() => {
    if (lookupContext?.type === "Facility") {
      return toFacilityPickerItems(lookupContext, facilityNotesData?.facilityNotes?.nodes ?? []);
    }

    if (lookupContext?.type === "Event") {
      return toEventPickerItems(lookupContext, eventNotesData?.eventNotes?.nodes ?? []);
    }

    const person = applicationsData?.person ?? null;
    const notes = notesData?.personNotes?.nodes ?? [];
    const interactions = interactionsData?.person?.interactions?.nodes ?? [];
    const applications = applicationsData?.person?.membershipApplications ?? [];
    const orders = ordersData?.commerceOrders?.nodes ?? [];
    const payments = paymentsData?.commercePayments?.nodes ?? [];
    const careerPlans = careerPlansData?.person?.careerPlans ?? [];
    return toPickerItems(person, notes, interactions, applications, orders, payments, careerPlans);
  }, [
    lookupContext,
    facilityNotesData,
    eventNotesData,
    notesData,
    interactionsData,
    applicationsData,
    ordersData,
    paymentsData,
    careerPlansData
  ]);

  const truncated =
    lookupContext?.type === "Facility"
      ? (facilityNotesData?.facilityNotes?.nodes?.length ?? 0) >= QUERY_LIMIT
      : lookupContext?.type === "Event"
        ? (eventNotesData?.eventNotes?.nodes?.length ?? 0) >= QUERY_LIMIT
        : (notesData?.personNotes?.nodes?.length ?? 0) >= QUERY_LIMIT ||
          (interactionsData?.person?.interactions?.nodes?.length ?? 0) >= QUERY_LIMIT ||
          (ordersData?.commerceOrders?.nodes?.length ?? 0) >= QUERY_LIMIT ||
          (paymentsData?.commercePayments?.nodes?.length ?? 0) >= QUERY_LIMIT;

  const error =
    lookupContext?.type === "Facility"
      ? facilityNotesError
      : lookupContext?.type === "Event"
        ? eventNotesError
        : undefined;

  return {
    items,
    loading:
      notesLoading ||
      interactionsLoading ||
      applicationsLoading ||
      ordersLoading ||
      paymentsLoading ||
      careerPlansLoading ||
      facilityNotesLoading ||
      eventNotesLoading,
    error,
    truncated
  };
}
